// A per-conversation journal of what each writer did to the transcript on screen: metadata
// only (kinds, counts, seq ranges, lives, sources), never message content. Console:
// `__cyborgTranscript.journal("<agentId>")`, `__cyborgTranscript.agents()`.

import { conversationSync } from "./sync-session.js";

export interface JournalEntry {
  at: number;
  writer: string;
  [field: string]: unknown;
}

const PER_AGENT = 200;
// Conversations kept at once; the least recently written is forgotten first.
const MAX_AGENTS = 50;
const journals = conversationSync.collection(
  "transcriptJournals",
  new Map<string, JournalEntry[]>(),
);

function entriesFor(agentId: string): JournalEntry[] {
  let entries = journals.get(agentId);
  if (entries) {
    // Re-insert: Map order is the recency order the global bound evicts by.
    journals.delete(agentId);
  } else {
    entries = [];
    if (journals.size >= MAX_AGENTS) journals.delete(journals.keys().next().value as string);
  }
  journals.set(agentId, entries);
  return entries;
}

function push(entries: JournalEntry[], entry: JournalEntry): void {
  entries.push(entry);
  if (entries.length > PER_AGENT) entries.splice(0, entries.length - PER_AGENT);
}

export function journal(agentId: string, writer: string, fields: Record<string, unknown>): void {
  push(entriesFor(agentId), { at: Date.now(), writer, ...fields });
}

// A frame-by-frame writer (a live row applied, a frame held while another life is
// adopted): one entry per RUN of the same writer and outcome, so a streamed reply of a
// thousand chunks is one line with its seq span, not a thousand that push out the rest.
export function journalRun(
  agentId: string,
  writer: string,
  outcome: string,
  seq: number | undefined,
): void {
  const entries = entriesFor(agentId);
  const last = entries.at(-1);
  if (last && last.writer === writer && last.outcome === outcome) {
    last.count = (last.count as number) + 1;
    last.last = Date.now();
    if (seq !== undefined) {
      last.from = Math.min((last.from as number | undefined) ?? seq, seq);
      last.to = Math.max((last.to as number | undefined) ?? seq, seq);
    } else last.noSeq = (last.noSeq as number) + 1;
    return;
  }
  push(entries, {
    at: Date.now(),
    writer,
    outcome,
    count: 1,
    from: seq,
    to: seq,
    noSeq: seq === undefined ? 1 : 0,
  });
}

export function transcriptJournal(agentId: string): readonly JournalEntry[] {
  return journals.get(agentId) ?? [];
}

export function clearTranscriptJournals(): void {
  journals.clear();
}

// What a writer is judged by: the committed rows the screen shows (every seq an entry
// carries, a reply's chunks included — not just the entry's own seq), and its prompts
// and replies for reading the journal.
export interface TranscriptShape {
  entries: number;
  prompts: number;
  replies: number;
  rows: number;
  minSeq: number | undefined;
  maxSeq: number | undefined;
}

export function screenRows<E extends { kind: string }>(
  entries: readonly E[],
  seqsOf: (entry: E) => readonly number[],
): Set<number> {
  const rows = new Set<number>();
  for (const e of entries) for (const seq of seqsOf(e)) rows.add(seq);
  return rows;
}

export function shapeOf(
  entries: ReadonlyArray<{ kind: string }>,
  rows: ReadonlySet<number>,
): TranscriptShape {
  let prompts = 0;
  let replies = 0;
  for (const e of entries) {
    if (e.kind === "user_message") prompts += 1;
    else if (e.kind === "text") replies += 1;
  }
  let minSeq: number | undefined;
  let maxSeq: number | undefined;
  for (const seq of rows) {
    if (minSeq === undefined || seq < minSeq) minSeq = seq;
    if (maxSeq === undefined || seq > maxSeq) maxSeq = seq;
  }
  return { entries: entries.length, prompts, replies, rows: rows.size, minSeq, maxSeq };
}

// The seq range a page covers.
export function pageRange(items: ReadonlyArray<Record<string, unknown>>): {
  rows: number;
  minSeq: number | undefined;
  maxSeq: number | undefined;
} {
  let minSeq: number | undefined;
  let maxSeq: number | undefined;
  for (const raw of items) {
    const seq = raw.seq;
    if (typeof seq !== "number") continue;
    if (minSeq === undefined || seq < minSeq) minSeq = seq;
    if (maxSeq === undefined || seq > maxSeq) maxSeq = seq;
  }
  return { rows: items.length, minSeq, maxSeq };
}

// The committed rows that were on screen and are not any more. Merging two fragments of a
// reply into one bubble loses none; dropping a row does — that is the incident class.
export function lostRows(
  before: ReadonlySet<number>,
  after: ReadonlySet<number>,
): { count: number; from: number | undefined; to: number | undefined } {
  let count = 0;
  let from: number | undefined;
  let to: number | undefined;
  for (const seq of before) {
    if (after.has(seq)) continue;
    count += 1;
    if (from === undefined || seq < from) from = seq;
    if (to === undefined || seq > to) to = seq;
  }
  return { count, from, to };
}

// The writer's call path as function names only: no file paths, URLs or arguments.
// The history facade's own frames name no writer: the call path skips them (#3382 A-2).
const FACADE_FRAME = /^(?:WriteJudge\.|ConversationHistory\.|(?:Object\.)?(?:judge|judgedBy)$)/;

export function callerFrames(): string | undefined {
  const stack = new Error("transcript write").stack;
  if (!stack) return undefined;
  const names = stack
    .split("\n")
    .slice(2)
    .map((line) => /at (?:async )?([\w$.<>]+)/.exec(line)?.[1] ?? /^([\w$.<>]+)@/.exec(line)?.[1])
    .filter((name): name is string => !!name && !FACADE_FRAME.test(name));
  return names.slice(0, 8).join(" < ");
}

if (typeof globalThis !== "undefined") {
  (globalThis as { __cyborgTranscript?: unknown }).__cyborgTranscript = {
    journal: (agentId: string) => transcriptJournal(agentId),
    agents: () => [...journals.keys()],
  };
}
